/**
 * 基于 /api/* 的全局状态（替代旧 useStore 的 mock localStorage 链路）。
 *
 * 仅持有「订阅列表 + 全局设置 + 推送历史 + 健康状态」这类需要跨页面共享、
 * 且来自后端的真实数据。行情/新闻预览等单页局部数据仍在各页面本地 state。
 *
 * 鉴权：API Key 由 api.ts 自行从 localStorage 读取并附加 X-API-Key 头。
 * 这里不再处理 401 引导（已在 ControlPanel 完成）。
 */
import { create } from "zustand";
import {
  SettingsApi,
  SubscriptionsApi,
  PushApi,
  HealthApi,
  type GlobalSettings,
  type Subscription,
  type PushHistoryItem,
  type HealthStatus,
  type PushStats,
  ApiError,
} from "@/services/api";

interface ApiState {
  settings: GlobalSettings | null;
  subscriptions: Subscription[];
  history: PushHistoryItem[];
  health: HealthStatus | null;
  loading: boolean;
  error: string;
  /** 是否已成功加载过一次（用于区分首次加载与刷新） */
  initialized: boolean;

  /** 拉取全部共享数据。force=true 时强制 loading 态 */
  refresh: (force?: boolean) => Promise<void>;

  // 订阅
  createSub: (code: string, name: string, mkt: string) => Promise<Subscription>;
  updateSub: (code: string, patch: Partial<Subscription>) => Promise<Subscription>;
  removeSub: (code: string) => Promise<void>;

  // 全局开关
  togglePushEnabled: () => Promise<void>;

  // 手动触发推送（默认合并模式）
  trigger: (force?: boolean, merge?: boolean) => Promise<PushStats>;
}

const wrap = (e: unknown, fallback: string): string =>
  e instanceof ApiError ? e.message : fallback;

export const useApiStore = create<ApiState>((set, get) => ({
  settings: null,
  subscriptions: [],
  history: [],
  health: null,
  loading: false,
  error: "",
  initialized: false,

  refresh: async (force = false) => {
    if (force) set({ loading: true });
    set({ error: "" });
    try {
      const [settings, subscriptions, history, health] = await Promise.all([
        SettingsApi.get(),
        SubscriptionsApi.list(),
        PushApi.history(7),
        HealthApi.check(),
      ]);
      set({
        settings,
        subscriptions,
        history,
        health,
        loading: false,
        initialized: true,
      });
    } catch (e) {
      set({
        loading: false,
        error: wrap(e, "加载失败"),
        initialized: true,
      });
      // 401 时清空数据，让上层引导重新填 Key
      if (e instanceof ApiError && e.status === 401) {
        set({ settings: null, subscriptions: [], history: [], health: null });
      }
    }
  },

  createSub: async (code, name, mkt) => {
    const sub = await SubscriptionsApi.create(code, name, mkt);
    set((s) => ({ subscriptions: [...s.subscriptions, sub] }));
    return sub;
  },

  updateSub: async (code, patch) => {
    const updated = await SubscriptionsApi.update(code, patch);
    set((s) => ({
      subscriptions: s.subscriptions.map((x) =>
        x.stockCode === code ? updated : x,
      ),
    }));
    return updated;
  },

  removeSub: async (code) => {
    await SubscriptionsApi.remove(code);
    set((s) => ({
      subscriptions: s.subscriptions.filter((x) => x.stockCode !== code),
    }));
  },

  togglePushEnabled: async () => {
    const cur = get().settings;
    if (!cur) return;
    const updated = await SettingsApi.update(!cur.pushEnabled);
    set({ settings: updated });
  },

  trigger: async (force = true, merge = true) => {
    const stats = await PushApi.trigger(force, merge);
    // 触发后刷新历史
    try {
      const history = await PushApi.history(7);
      set({ history });
    } catch {
      /* 历史刷新失败不影响主结果 */
    }
    return stats;
  },
}));
